import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import React, { forwardRef } from 'react';
import { Segment, isValidPostcode } from '@fixter/i18n';
import styled from 'styled-components';
import classNames from 'classnames';
import * as Yup from 'yup';
import FormItem from '$components/composite/Form/FormItem';
import PostcodeInput from '$components/composite/Form/DynamicRoutePostcodeInput';
import Input from '$components/composite/Form/InputCmsIntegrated';
import { postcodeRegex, vrmFRRegex, vrmUKRegex } from '$constants/regexp';
import type { AnyObject } from '$util/types';
import { trimSpacesFromString } from '$util/stringOperations/trimSpacesFromString';
import { Tag } from '$quantum/atoms/Tag';
import { vars } from '$util/theme';
import { Button } from '../../../src/components/atoms/Button';
import { DesignType } from '../../../src/components/atoms/Button/constants';
import Styled from './styled';

// This is temporarry for the initial reskin untill we get rid of antd and replace all input fields
const StyledVrmInput = styled(Input)`
  input {
    color: ${vars.quantumverse.reskinColors.neutral600};
    border-radius: 30px;
    transition: 0;

    &::placeholder {
      color: ${vars.quantumverse.reskinColors.neutral400} !important;
    }
  }
`;

export interface ValuePayload {
  vrm: string;
  postcode: string;
}

export interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly invalidMessage?: string;
  readonly missingMessage?: string;
}

export interface DynamicRouteVrmLookupProps {
  readonly className?: string;
  readonly segment?: Segment;
  readonly postcodeField: FieldProps;
  readonly vrmField: FieldProps;
  readonly disabled?: boolean;
  readonly mainCta?: string;
  readonly tagText?: string;
  readonly onSubmitForm: (payload: AnyObject) => Promise<void>;
  readonly changeValueFunction: Dispatch<SetStateAction<ValuePayload>>;
}

const getValidationSchema = ({
  invalidVRMMessage,
  missingVRMMessage,
  invalidPostcodeMessage,
  missingPostcodeMessage,
  segment,
}: AnyObject) => {
  if (segment === Segment.Fixter_FR) {
    return Yup.object().shape({
      vrm: Yup.string().trim().matches(vrmFRRegex, invalidVRMMessage).required(missingVRMMessage),
      postcode: Yup.mixed()
        .test({
          name: 'postcodeValidation',
          test: (value) => !!value && isValidPostcode(trimSpacesFromString(value), segment),
          message: invalidPostcodeMessage,
        })
        .required(missingPostcodeMessage),
    });
  }

  return Yup.object().shape({
    vrm: Yup.string().trim().matches(vrmUKRegex, invalidVRMMessage).required(missingVRMMessage),
    postcode: Yup.string()
      .uppercase()
      .trim()
      // including +1 white space (trim doesn't seem to work as expected here)
      .max(8, invalidPostcodeMessage)
      .matches(postcodeRegex, invalidPostcodeMessage)
      .required(missingPostcodeMessage),
  });
};

export const DynamicRouteVrmLookup = forwardRef(
  (
    {
      disabled = false,
      segment,
      className,
      mainCta = 'Get an instant quoute',
      postcodeField = { label: 'Your postcode', value: '' },
      vrmField = { label: 'Your registration number', value: '' },
      tagText,
      onSubmitForm,
      changeValueFunction,
      ...props
    }: DynamicRouteVrmLookupProps,
    ref: React.Ref<any>
  ): JSX.Element => {
    const validationData = {
      invalidVRMMessage: vrmField.invalidMessage,
      missingVRMMessage: vrmField.missingMessage,
      invalidPostcodeMessage: postcodeField.invalidMessage,
      missingPostcodeMessage: postcodeField.missingMessage,
      segment,
    };

    const disableAutocorrect = segment === Segment.Fixter_FR;

    return (
      <Styled
        {...props}
        formik={{
          initialValues: { vrmValue: vrmField.value, postcodeValue: postcodeField.value },
          validationSchema: getValidationSchema(validationData),
          onSubmit: onSubmitForm,
        }}
        className={classNames(DynamicRouteVrmLookup.displayName, className)}
      >
        <div ref={ref}>
          <div className="input-group">
            <FormItem name="postcode" className="postcode">
              <PostcodeInput
                useAutocomplete={segment === Segment.Fixter_UK}
                placeholder={postcodeField.label}
                spellCheck={!disableAutocorrect}
                onChange={(e: string) => changeValueFunction({ vrm: vrmField.value, postcode: e })}
              />
            </FormItem>
            <FormItem name="vrm" className="vrm">
              <StyledVrmInput
                className="vrm-input"
                spellCheck={!disableAutocorrect}
                autoComplete={disableAutocorrect ? 'off' : 'on'}
                name="vrm"
                label={vrmField.label}
                value={vrmField.value}
                iconId="icon_car"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  changeValueFunction({ vrm: e?.target?.value, postcode: postcodeField.value })
                }
              />
            </FormItem>
          </div>

          <div className="footer-group">
            <Button
              disabled={disabled}
              designType={DesignType.PRIMARY}
              size="L"
              fullWidth
              className="-submit"
              type="submit"
            >
              {mainCta}
              {tagText && <Tag>{tagText}</Tag>}
            </Button>
          </div>
        </div>
      </Styled>
    );
  }
);

DynamicRouteVrmLookup.displayName = 'DynamicRouteVrmLookup';
